import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Edit, MapPin, Package, TrendingUp, Activity, Plus, Building, Phone, Mail, Clock, Users } from 'lucide-react';
import WarehouseService from '@/api/warehouses.service';
import { canEdit, canCreate, canView } from '@/lib/permissions';

interface Warehouse {
  publicId: string;
  name: string;
  code: string;
  description?: string;
  address?: string;
  contactPerson?: string;
  contactPhone?: string;
  contactEmail?: string;
  capacity?: number;
  timezone?: string;
  status: 'active' | 'inactive';
  createdAt: string;
  updatedAt: string;
}

interface WarehouseLocation {
  publicId: string;
  code: string;
  name: string;
  type: string;
  capacity?: number;
  currentStock?: number;
  status: 'active' | 'inactive';
}

interface StockSummary {
  totalItems: number;
  totalValue: number;
  lowStockItems: number;
  outOfStockItems: number;
}

interface PerformanceMetrics {
  utilizationPercentage: number;
  turnoverRate: number;
  accuracyRate: number;
  cycleTime: number;
}

const WarehouseDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  
  const [warehouse, setWarehouse] = useState<Warehouse | null>(null);
  const [locations, setLocations] = useState<WarehouseLocation[]>([]);
  const [stockSummary, setStockSummary] = useState<StockSummary>({
    totalItems: 0,
    totalValue: 0,
    lowStockItems: 0,
    outOfStockItems: 0
  });
  const [performance, setPerformance] = useState<PerformanceMetrics>({
    utilizationPercentage: 0,
    turnoverRate: 0,
    accuracyRate: 0,
    cycleTime: 0
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (id) {
      fetchWarehouseData();
    }
  }, [id]);

  const fetchWarehouseData = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      
      // Fetch warehouse details
      const warehouseResponse = await WarehouseService.getWarehouse(id);
      if (warehouseResponse.data?.success) {
        setWarehouse(warehouseResponse.data.warehouse);
      }

      // Fetch warehouse locations
      try {
        const locationsResponse = await WarehouseService.getWarehouseLocations(id);
        if (locationsResponse.data?.success) {
          setLocations(locationsResponse.data.locations || []);
        }
      } catch (error) {
        console.log('Locations not available:', error);
      }

      // Fetch stock summary
      try {
        const stockResponse = await WarehouseService.getWarehouseStockSummary(id);
        if (stockResponse.data?.success) {
          setStockSummary(stockResponse.data.summary || stockSummary);
        }
      } catch (error) {
        console.log('Stock summary not available:', error);
      }

      // Fetch performance metrics
      try {
        const performanceResponse = await WarehouseService.getWarehousePerformance(id);
        if (performanceResponse.data?.success) {
          setPerformance(performanceResponse.data.metrics || performance);
        }
      } catch (error) {
        console.log('Performance metrics not available:', error);
      }

    } catch (error) {
      console.error('Error fetching warehouse data:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch warehouse details",
      });
      navigate('/warehouses');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    return status === 'active' 
      ? <Badge variant="default" className="bg-green-100 text-green-800">Active</Badge>
      : <Badge variant="secondary" className="bg-red-100 text-red-800">Inactive</Badge>;
  };

  const formatCapacity = (capacity?: number) => {
    if (!capacity) return 'N/A';
    return capacity >= 1000 ? `${(capacity / 1000).toFixed(1)}K` : capacity.toString();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!warehouse) {
    return (
      <div className="text-center py-8">
        <h3 className="text-lg font-medium text-gray-900">Warehouse not found</h3>
        <p className="text-gray-500">The warehouse you're looking for doesn't exist.</p>
        <Button onClick={() => navigate('/warehouses')} className="mt-4">
          Back to Warehouses
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/warehouses')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Warehouses
          </Button>
        </div>
        <div className="flex gap-2">
          {canEdit('Warehouse') && (
            <Button onClick={() => navigate(`/warehouses/${warehouse.publicId}/edit`)}>
              <Edit className="h-4 w-4 mr-2" />
              Edit Warehouse
            </Button>
          )}
          {canCreate('Warehouse') && (
            <Button variant="outline" onClick={() => navigate(`/warehouse/locations/create?warehouseId=${warehouse.publicId}`)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Location
            </Button>
          )}
        </div>
      </div>

      {/* Warehouse Header Info */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-2xl">{warehouse.name}</CardTitle>
              <CardDescription className="text-lg">
                Code: {warehouse.code}
              </CardDescription>
            </div>
            {getStatusBadge(warehouse.status)}
          </div>
          {warehouse.description && (
            <p className="text-muted-foreground mt-2">{warehouse.description}</p>
          )}
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {warehouse.address && (
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground mt-1" />
                <div>
                  <p className="text-sm font-medium">Address</p>
                  <p className="text-sm text-muted-foreground">{warehouse.address}</p>
                </div>
              </div>
            )}
            {warehouse.contactPerson && (
              <div className="flex items-start gap-2">
                <Users className="h-4 w-4 text-muted-foreground mt-1" />
                <div>
                  <p className="text-sm font-medium">Contact Person</p>
                  <p className="text-sm text-muted-foreground">{warehouse.contactPerson}</p>
                </div>
              </div>
            )}
            {warehouse.contactPhone && (
              <div className="flex items-start gap-2">
                <Phone className="h-4 w-4 text-muted-foreground mt-1" />
                <div>
                  <p className="text-sm font-medium">Phone</p>
                  <p className="text-sm text-muted-foreground">{warehouse.contactPhone}</p>
                </div>
              </div>
            )}
            {warehouse.contactEmail && (
              <div className="flex items-start gap-2">
                <Mail className="h-4 w-4 text-muted-foreground mt-1" />
                <div>
                  <p className="text-sm font-medium">Email</p>
                  <p className="text-sm text-muted-foreground">{warehouse.contactEmail}</p>
                </div>
              </div>
            )}
            {warehouse.capacity && (
              <div className="flex items-start gap-2">
                <Package className="h-4 w-4 text-muted-foreground mt-1" />
                <div>
                  <p className="text-sm font-medium">Capacity</p>
                  <p className="text-sm text-muted-foreground">{formatCapacity(warehouse.capacity)}</p>
                </div>
              </div>
            )}
            {warehouse.timezone && (
              <div className="flex items-start gap-2">
                <Clock className="h-4 w-4 text-muted-foreground mt-1" />
                <div>
                  <p className="text-sm font-medium">Timezone</p>
                  <p className="text-sm text-muted-foreground">{warehouse.timezone}</p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="locations">Locations ({locations.length})</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="activities">Activities</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* Stock Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Items</CardTitle>
                <Package className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stockSummary.totalItems}</div>
                <p className="text-xs text-muted-foreground">Items in stock</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Value</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatCurrency(stockSummary.totalValue)}</div>
                <p className="text-xs text-muted-foreground">Inventory value</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Low Stock</CardTitle>
                <Activity className="h-4 w-4 text-orange-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600">{stockSummary.lowStockItems}</div>
                <p className="text-xs text-muted-foreground">Items below threshold</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Out of Stock</CardTitle>
                <Activity className="h-4 w-4 text-red-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">{stockSummary.outOfStockItems}</div>
                <p className="text-xs text-muted-foreground">Items out of stock</p>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
              <CardDescription>Common warehouse operations</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {canView('Warehouse') && (
                  <Button variant="outline" onClick={() => navigate('/warehouse/transfers')}>
                    View Transfers
                  </Button>
                )}
                {canView('Warehouse') && (
                  <Button variant="outline" onClick={() => navigate('/warehouse/locations')}>
                    Manage Locations
                  </Button>
                )}
                {canCreate('Warehouse') && (
                  <Button variant="outline" onClick={() => navigate('/warehouse/transfers/create')}>
                    Create Transfer
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="locations" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium">Warehouse Locations</h3>
            {canCreate('Warehouse') && (
              <Button onClick={() => navigate(`/warehouse/locations/create?warehouseId=${warehouse.publicId}`)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Location
              </Button>
            )}
          </div>

          {locations.length === 0 ? (
            <Card>
              <CardContent className="text-center py-8">
                <Building className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No locations found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Get started by creating your first warehouse location.
                </p>
                {canCreate('Warehouse') && (
                  <div className="mt-6">
                    <Button onClick={() => navigate(`/warehouse/locations/create?warehouseId=${warehouse.publicId}`)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Location
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {locations.map((location) => (
                <Card key={location.publicId}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">{location.name}</CardTitle>
                        <CardDescription>Code: {location.code}</CardDescription>
                      </div>
                      {getStatusBadge(location.status)}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <p className="text-sm"><strong>Type:</strong> {location.type}</p>
                      {location.capacity && (
                        <p className="text-sm"><strong>Capacity:</strong> {formatCapacity(location.capacity)}</p>
                      )}
                      {location.currentStock !== undefined && (
                        <p className="text-sm"><strong>Current Stock:</strong> {location.currentStock}</p>
                      )}
                      {canView('Warehouse') && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => navigate(`/warehouse/locations/${location.publicId}`)}
                          className="w-full mt-2"
                        >
                          View Details
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="performance" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Utilization</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{performance.utilizationPercentage}%</div>
                <p className="text-xs text-muted-foreground">Capacity utilization</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Turnover Rate</CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{performance.turnoverRate.toFixed(1)}</div>
                <p className="text-xs text-muted-foreground">Times per year</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Accuracy Rate</CardTitle>
                <Package className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{performance.accuracyRate}%</div>
                <p className="text-xs text-muted-foreground">Inventory accuracy</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Cycle Time</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{performance.cycleTime}</div>
                <p className="text-xs text-muted-foreground">Hours average</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="activities" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Recent Activities</CardTitle>
              <CardDescription>Latest warehouse operations and changes</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8">
                <Activity className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No recent activities</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Activities will appear here as they occur.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default WarehouseDetail; 